import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import type { IPublicClientApplication } from "@azure/msal-browser";
import { Center, Loader, Stack, Text } from "@mantine/core";
import type { ReactNode } from "react";
import { formatDate } from "../../lib/format";
import { EmptyState, ErrorState, GalleryGrid, LoadingState } from "../ui";

const PAGE_SIZE = 24;
// Offset (≈ header height) used to decide which page is "at the top" for the URL.
const HEADER_OFFSET = 80;

interface GalleryItemBase {
  id: string;
  createdAt: string;
}

type PageBlock<T> = { page: number; items: T[] };

export interface GalleryItemActions<T> {
  replace: (item: T) => void;
  remove: (id: string) => void;
}

function groupByDate<T extends GalleryItemBase>(items: T[]): [string, T[]][] {
  const groups: Record<string, T[]> = {};
  for (const item of items) {
    const date = item.createdAt.slice(0, 10);
    groups[date] ??= [];
    groups[date].push(item);
  }
  return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
}

/** `?page=` is optional (absent = first page); when present it must be a positive integer. */
function parsePageParam(raw: string | null): number {
  if (raw === null) return 1;
  const page = Number(raw);
  if (!Number.isInteger(page) || page < 1) throw new Error(`Invalid page parameter: "${raw}"`);
  return page;
}

interface DateGroupedGalleryProps<T extends GalleryItemBase> {
  /** Fetches one page of items; the component owns paging, URL sync and infinite scroll. */
  fetchPage: (
    msal: IPublicClientApplication,
    page: number,
    pageSize: number,
    signal?: AbortSignal,
  ) => Promise<{ items: T[]; total: number }>;
  /** Renders one tile; `actions` let the tile's own operations (delete, retry) update the list in place. */
  renderItem: (item: T, actions: GalleryItemActions<T>) => ReactNode;
  emptyMessage: string;
  /** Called whenever the total count is (re)loaded, so the page header can show it. */
  onTotal: (total: number) => void;
}

/**
 * Bidirectional infinite-scroll gallery grouped by creation date. The landing page comes from
 * `?page=`; scrolling loads the neighbouring pages (sentinels above and below) and keeps the
 * URL on the page currently under the header. Shared by the pillow and ornament galleries.
 */
export default function DateGroupedGallery<T extends GalleryItemBase>({
  fetchPage,
  renderItem,
  emptyMessage,
  onTotal,
}: DateGroupedGalleryProps<T>) {
  const { instance } = useMsal();
  const [searchParams, setSearchParams] = useSearchParams();

  // The page we land on. Captured once: scrolling updates the URL but must not
  // re-trigger the initial fetch.
  const initialPage = parsePageParam(searchParams.get("page"));
  const initialPageRef = useRef(initialPage);

  const [blocks, setBlocks] = useState<PageBlock<T>[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingDir, setLoadingDir] = useState<"up" | "down" | null>(null);

  const loadingRef = useRef(false);
  const positionedRef = useRef(false);
  const blockEls = useRef(new Map<number, HTMLDivElement>());
  const lastUrlPageRef = useRef(initialPage);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = blocks.length > 0 && blocks[0].page > 1;
  const hasNext = blocks.length > 0 && blocks[blocks.length - 1].page < totalPages;

  useEffect(() => onTotal(total), [total, onTotal]);

  // ── Initial load: only the landing page (never the ones before it). ──────────
  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    fetchPage(instance, initialPageRef.current, PAGE_SIZE, controller.signal)
      .then((data) => {
        if (ignore) return;
        const tp = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
        // Stale deep link past the end → fall back to the last real page.
        if (initialPageRef.current > tp) {
          initialPageRef.current = tp;
          lastUrlPageRef.current = tp;
          return fetchPage(instance, tp, PAGE_SIZE, controller.signal).then((d2) => {
            if (!ignore) {
              setBlocks([{ page: tp, items: d2.items }]);
              setTotal(d2.total);
            }
          });
        }
        setBlocks([{ page: initialPageRef.current, items: data.items }]);
        setTotal(data.total);
      })
      .catch((err: Error) => {
        if (!ignore && err.name !== "AbortError") {
          setError(err.message);
          setBlocks([]);
        }
      });

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [instance, fetchPage]);

  // ── Position the landing page under the header (so there is room to scroll
  //    up into earlier pages). Runs before paint to avoid a flash. ─────────────
  useLayoutEffect(() => {
    if (positionedRef.current || blocks.length === 0) return;
    positionedRef.current = true;
    const el = blockEls.current.get(initialPageRef.current);
    if (el && initialPageRef.current > 1) {
      const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
      window.scrollTo(0, Math.max(0, y));
    }
  }, [blocks]);

  const loadNext = useCallback(async () => {
    if (loadingRef.current || !positionedRef.current) return;
    const last = blocks[blocks.length - 1];
    if (!last || last.page >= Math.max(1, Math.ceil(total / PAGE_SIZE))) return;

    loadingRef.current = true;
    setLoadingDir("down");
    try {
      const data = await fetchPage(instance, last.page + 1, PAGE_SIZE);
      setBlocks((b) => (b[b.length - 1]?.page === last.page ? [...b, { page: last.page + 1, items: data.items }] : b));
      setTotal(data.total);
    } catch (err) {
      if (err instanceof Error) setError(err.message);
    } finally {
      loadingRef.current = false;
      setLoadingDir(null);
    }
  }, [instance, fetchPage, blocks, total]);

  const loadPrev = useCallback(async () => {
    if (loadingRef.current || !positionedRef.current) return;
    const first = blocks[0];
    if (!first || first.page <= 1) return;

    loadingRef.current = true;
    setLoadingDir("up");
    const scroller = document.scrollingElement;
    if (scroller === null) throw new Error("Document has no scrolling element");
    const beforeHeight = scroller.scrollHeight;
    const beforeTop = scroller.scrollTop;
    try {
      const data = await fetchPage(instance, first.page - 1, PAGE_SIZE);
      setBlocks((b) => (b[0]?.page === first.page ? [{ page: first.page - 1, items: data.items }, ...b] : b));
      setTotal(data.total);
      // Keep the viewport on the same content after prepending above it.
      requestAnimationFrame(() => {
        scroller.scrollTop = beforeTop + (scroller.scrollHeight - beforeHeight);
      });
    } catch (err) {
      if (err instanceof Error) setError(err.message);
    } finally {
      loadingRef.current = false;
      setLoadingDir(null);
    }
  }, [instance, fetchPage, blocks]);

  // ── Trigger loads when the sentinels come into view. ────────────────────────
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    if (topSentinelRef.current) {
      const o = new IntersectionObserver(([e]) => e.isIntersecting && void loadPrev(), { rootMargin: "0px" });
      o.observe(topSentinelRef.current);
      observers.push(o);
    }
    if (bottomSentinelRef.current) {
      const o = new IntersectionObserver(([e]) => e.isIntersecting && void loadNext(), { rootMargin: "400px 0px" });
      o.observe(bottomSentinelRef.current);
      observers.push(o);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [loadPrev, loadNext]);

  // ── Keep the URL in sync with the page currently at the top of the viewport. ─
  useEffect(() => {
    if (blocks.length === 0) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        let current = blocks[0].page;
        for (const b of blocks) {
          const el = blockEls.current.get(b.page);
          if (el && el.getBoundingClientRect().top <= HEADER_OFFSET + 4) current = b.page;
        }
        if (current !== lastUrlPageRef.current) {
          lastUrlPageRef.current = current;
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              if (current <= 1) next.delete("page");
              else next.set("page", String(current));
              return next;
            },
            { replace: true },
          );
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [blocks, setSearchParams]);

  const itemActions: GalleryItemActions<T> = {
    replace: (item) =>
      setBlocks((b) => b.map((block) => ({ ...block, items: block.items.map((i) => (i.id === item.id ? item : i)) }))),
    remove: (id) => {
      setBlocks((b) => b.map((block) => ({ ...block, items: block.items.filter((i) => i.id !== id) })));
      setTotal((t) => Math.max(0, t - 1));
    },
  };

  // An empty gallery still holds one block with zero items, so "empty" must be judged by the
  // item count, not blocks.length — otherwise the empty page renders blank instead of EmptyState.
  const itemCount = blocks.reduce((sum, block) => sum + block.items.length, 0);
  const loading = blocks.length === 0 && !error;

  if (loading) return <LoadingState h={200} />;
  if (error && itemCount === 0) return <ErrorState message={error} />;
  if (itemCount === 0) return <EmptyState message={emptyMessage} />;

  return (
    <>
      {hasPrev && (
        <Center ref={topSentinelRef} h={48}>
          {loadingDir === "up" && <Loader size="sm" />}
        </Center>
      )}

      {blocks.map((block) => (
        <div
          key={block.page}
          ref={(el) => {
            if (el) blockEls.current.set(block.page, el);
            else blockEls.current.delete(block.page);
          }}
        >
          <Stack gap="lg">
            {groupByDate(block.items).map(([date, dateItems]) => (
              <Stack key={`${block.page}-${date}`} gap="sm">
                <Text fw={600} c="dimmed" size="sm">
                  {formatDate(date)}
                </Text>
                <GalleryGrid cols={{ base: 2, xs: 3, sm: 4, md: 6 }}>
                  {dateItems.map((item) => renderItem(item, itemActions))}
                </GalleryGrid>
              </Stack>
            ))}
          </Stack>
        </div>
      ))}

      {error && <ErrorState message={error} />}

      {hasNext && (
        <Center ref={bottomSentinelRef} h={48}>
          {loadingDir === "down" && <Loader size="sm" />}
        </Center>
      )}
    </>
  );
}
